import React from 'react';

/* ==========================================================
   Cross-role notification bus (localStorage-backed)
   Emits pub/sub-style events between prototype roles.
   ========================================================== */

const KEY = 'assistivos_notifications_v1';
const listeners = new Set();

const read = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch (_e) { /* ignore */ }
  return [];
};

const write = (list) => {
  try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (_e) { /* ignore */ }
  listeners.forEach((fn) => { try { fn(list); } catch (_e) { /* ignore */ } });
};

/**
 * Post a new notification.
 *   from, to  — role keys ('facility_admin', 'training_provider', ...)
 *   type      — semantic tag ('course_approved', 'flagged_learner', ...)
 *   title     — short headline
 *   body      — detail line
 *   meta      — freeform extra data
 */
export const postNotification = ({ from, to, type, title, body, meta }) => {
  const list = read();
  const notif = {
    id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    from, to, type, title, body,
    meta: meta || {},
    at: new Date().toISOString(),
    read: false,
  };
  write([notif, ...list].slice(0, 50)); // cap to 50 most recent
  return notif;
};

export const listNotifications = (toRole) => {
  const list = read();
  return toRole ? list.filter((n) => n.to === toRole) : list;
};

export const unreadCount = (toRole) => listNotifications(toRole).filter((n) => !n.read).length;

export const markRead = (id) => {
  const list = read();
  write(list.map((n) => (n.id === id ? { ...n, read: true } : n)));
};

export const markAllRead = (toRole) => {
  const list = read();
  write(list.map((n) => (n.to === toRole ? { ...n, read: true } : n)));
};

/**
 * React hook — subscribe to bus changes for a given target role.
 * Returns [notifications, unreadCount].
 */
export const useNotifications = (toRole) => {
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const fn = () => setTick((t) => t + 1);
    listeners.add(fn);
    // Cross-tab sync via storage event
    const onStorage = (e) => { if (e.key === KEY) fn(); };
    window.addEventListener('storage', onStorage);
    return () => { listeners.delete(fn); window.removeEventListener('storage', onStorage); };
  }, []);
  // `tick` is intentionally a dependency: it forces the memo to recompute
  // whenever the pub/sub bus fires. It never appears in the body.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = React.useMemo(() => listNotifications(toRole), [toRole, tick]);
  const unread = React.useMemo(() => items.filter((n) => !n.read).length, [items]);
  return [items, unread];
};
